import {
  Calendar,
  File,
  FileMinus,
  TableProperties,
  User,
  Wrench,
} from "lucide-react";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { appMessages } from "@/app/app.intl";
import { ROUTES } from "@/app/constants/routes_sidebar/routes.constant";
import { TSidebarItem } from "@/app/constants/routes_sidebar/sidebarNav.types";
import Icons from "@/components/Icons";
import { Roles } from "@/features/auth/auth.types";
import {
  useCustomerPortalPermission,
  useCustomerWritePermission,
} from "@/features/customerPortal/pages/shared/hooks/useCustomerPermission";
import { useMaintenanceBypassAccess } from "@/features/maintenance";
import { Permissions } from "@/interfaces/globals";
import { RootState, useAppSelector } from "@/redux/store";

const HIDDEN_BUSINESS_SIDEBAR_VALUES = new Set<string>([
  ROUTES.CALENDAR,
  ROUTES.SUPPORT,
  ROUTES.FLEET,
  ROUTES.TOOLS,
  ROUTES.WARE_HOUSING,
]);

export const useAdminSidebarData = (): TSidebarItem[] => {
  const { formatMessage } = useIntl();
  const canAccessMaintenancePage = useMaintenanceBypassAccess();

  return useMemo((): TSidebarItem[] => {
    const adminSidebarItems: TSidebarItem[] = [
      {
        icon: <Icons size={20} name='workspace' />,
        text: formatMessage(appMessages.sidebarAdminWorkspaces),
        value: "/",
        alterValue: "/Table",
        alterIcon: <TableProperties size={20} strokeWidth={1.5} />,
        alterText: formatMessage(appMessages.sidebarAdminWorkspacesTable),
        hasChild: false,
        permission: [],
      },
      {
        icon: <Icons size={20} name='workforce' />,
        text: formatMessage(appMessages.sidebarAdminWorkforce),
        value: `${ROUTES.WORKFORCE}`,
        hasChild: true,
        children: [
          {
            text: formatMessage(appMessages.sidebarAdminSubAdmin),
            active: false,
            value: `${ROUTES.ADMIN}`,
            permission: [Permissions.WORKFORCE_ADMIN],
          },
          {
            text: formatMessage(appMessages.sidebarAdminSubManager),
            active: false,
            value: `${ROUTES.MANAGER}`,
            permission: [Permissions.WORKFORCE_MANAGER],
          },
          {
            text: formatMessage(appMessages.sidebarAdminSubEmployee),
            active: false,
            value: `${ROUTES.EMPLOYEE}`,
            permission: [Permissions.WORKFORCE_EMPLOYEE],
          },
          {
            text: formatMessage(appMessages.sidebarAdminSubSubContractor),
            active: false,
            value: `${ROUTES.SUB_CONTRACTOR}`,
            permission: [Permissions.WORKFORCE_SUBCONTRACTOR],
          },
        ],
        permission: [Permissions.WORKFORCE],
      },
      {
        icon: <User size={20} strokeWidth={1.5} />,
        text: formatMessage(appMessages.sidebarAdminCustomers),
        value: `${ROUTES.CUSTOMERS}`,
        alert: true,
        hasChild: false,
        permission: [Permissions.CUSTOMER_CUSTOMER],
      },
    ];

    if (canAccessMaintenancePage) {
      adminSidebarItems.push({
        icon: <Wrench size={20} strokeWidth={1.5} />,
        text: formatMessage(appMessages.sidebarAdminMaintenance),
        value: `${ROUTES.MAINTENANCE}`,
        hasChild: false,
        permission: [],
      });
    }

    return adminSidebarItems;
  }, [canAccessMaintenancePage, formatMessage]);
};

export const useBusinessSidebarData = (): TSidebarItem[] => {
  const { formatMessage } = useIntl();
  const isSuperAdminUser = useAppSelector(
    (state: RootState) => state.user.userData?.role === Roles.SUPER_ADMIN,
  );

  return useMemo(() => {
    const businessSidebarItems: TSidebarItem[] = [
      {
        icon: <Icons size={20} name='roster-icon' />,
        text: formatMessage(appMessages.sidebarRoster),
        value: "/",
        hasChild: false,
        permission: [Permissions.ROSTER],
      },
      {
        icon: <Icons size={20} name='suitcase' />,
        text: formatMessage(appMessages.sidebarProjectRegister),
        value: `${ROUTES.PROJECT_REGISTER}`,
        alert: false,
        hasChild: false,
        permission: [Permissions.PROJECT_REGISTER],
      },
      {
        icon: <Icons size={20} name='estimate' />,
        text: formatMessage(appMessages.sidebarEstimates),
        value: `${ROUTES.ESTIMATES}`,
        active: false,
        hasChild: false,
        permission: [Permissions.ESTIMATES],
      },
      {
        icon: <Icons size={20} name='projects' />,
        text: formatMessage(appMessages.sidebarProjects),
        value: `${ROUTES.PROJECTS}`,
        hasChild: false,
        permission: [Permissions.PROJECTS],
      },
      {
        icon: <Icons size={20} name='projects' />,
        text: formatMessage(appMessages.sidebarTaskAndClaims),
        value: `${ROUTES.TASK_CLAIMS}`,
        hasChild: false,
        permission: [Permissions.CLAIMS],
      },
      {
        icon: <Icons size={20} name='workforce' />,
        text: formatMessage(appMessages.sidebarWorkforce),
        value: `${ROUTES.WORKFORCE}`,
        hasChild: true,
        permission: [
          Permissions.WORKFORCE,
          Permissions.WORKFORCE_ADMIN,
          Permissions.WORKFORCE_EMPLOYEE,
          Permissions.WORKFORCE_MANAGER,
          Permissions.WORKFORCE_SUBCONTRACTOR,
          Permissions.WORKFORCE_SETTINGS,
        ],
        children: [
          {
            text: formatMessage(appMessages.sidebarAdmin),
            active: false,
            value: `${ROUTES.ADMIN}`,
            permission: [Permissions.WORKFORCE_ADMIN],
          },
          {
            text: formatMessage(appMessages.sidebarManager),
            active: false,
            value: `${ROUTES.MANAGER}`,
            permission: [Permissions.WORKFORCE_MANAGER],
          },
          {
            text: formatMessage(appMessages.sidebarEmployee),
            active: false,
            value: `${ROUTES.EMPLOYEE}`,
            permission: [Permissions.WORKFORCE_EMPLOYEE],
          },
          {
            text: formatMessage(appMessages.sidebarSubContractor),
            active: false,
            value: `${ROUTES.SUB_CONTRACTOR}`,
            permission: [Permissions.WORKFORCE_SUBCONTRACTOR],
          },
          {
            text: formatMessage(appMessages.sidebarSettings),
            active: false,
            value: `${ROUTES.WORKFORCE_SETTINGS}`,
            permission: [Permissions.WORKFORCE_SETTINGS],
          },
        ],
      },
      {
        icon: <User size={20} />,
        text: formatMessage(appMessages.sidebarClients),
        value: `${ROUTES.CUSTOMERS}`,
        hasChild: true,
        permission: [
          Permissions.CUSTOMER,
          Permissions.CUSTOMER_CUSTOMER,
          Permissions.CUSTOMER_PROSPECT,
        ],
        children: [
          {
            text: formatMessage(appMessages.sidebarCustomer),
            active: false,
            value: `${ROUTES.CUSTOMER}`,
            permission: [Permissions.CUSTOMER_CUSTOMER],
          },
          {
            text: formatMessage(appMessages.sidebarProspect),
            active: false,
            value: `${ROUTES.PROSPECT}`,
            permission: [Permissions.CUSTOMER_PROSPECT],
          },
        ],
      },
      {
        icon: <Icons size={20} name='template' />,
        text: formatMessage(appMessages.sidebarInvoicing),
        value: `${ROUTES.INVOICING}`,
        hasChild: true,
        permission: [
          Permissions.INVOICE,
          Permissions.INVOICE_CONTRACT,
          Permissions.INVOICE_DOCKET,
          Permissions.INVOICE_XERO,
        ],
        children: [
          {
            text: formatMessage(appMessages.sidebarContractInvoice),
            active: false,
            value: `${ROUTES.CONTRACT_INVOICE}`,
            permission: [Permissions.INVOICE_CONTRACT],
          },
          {
            text: formatMessage(appMessages.sidebarDocketInvoice),
            active: false,
            value: `${ROUTES.DOCKET_INVOICE}`,
            permission: [Permissions.INVOICE_DOCKET],
          },
          {
            text: formatMessage(appMessages.sidebarXeroInvoice),
            active: false,
            value: `${ROUTES.XERO_INVOICE}`,
            permission: [Permissions.INVOICE_XERO],
          },
        ],
      },
      {
        icon: <Icons size={20} name='waste-manage' />,
        text: formatMessage(appMessages.sidebarWasteManage),
        value: `${ROUTES.WASTE}`,
        hasChild: true,
        permission: [
          Permissions.WASTEMANAGE,
          Permissions.WASTEMANAGE_SUMMARY,
          Permissions.WASTEMANAGE_REQUEST,
          Permissions.WASTEMANAGE_WEIGHTSCALE,
          Permissions.WASTEMANAGE_WEIGHTINGREPORTS,
          Permissions.WASTEMANAGE_VEHICLES,
          Permissions.WASTEMANAGE_LOADTYPE,
          Permissions.WASTEMANAGE_WASTETYPE,
          Permissions.WASTEMANAGE_DAILYREPORTS,
          Permissions.WASTEMANAGE_SETTINGS,
          Permissions.WASTEMANAGE_REQUEST,
        ],
        children: [
          {
            text: formatMessage(appMessages.sidebarSummary),
            active: false,
            value: `${ROUTES.SUMMARY}`,
            permission: [Permissions.WASTEMANAGE_SUMMARY],
          },
          {
            text: formatMessage(appMessages.sidebarRequest),
            active: false,
            value: `${ROUTES.WASTE_REQUEST}`,
            permission: [Permissions.WASTEMANAGE_REQUEST],
          },
          {
            text: formatMessage(appMessages.sidebarWeighBridge),
            active: false,
            value: `${ROUTES.WEIGH_BRIDGE}`,
            permission: [Permissions.WASTEMANAGE_WEIGHBRIDGE],
          },
          {
            text: formatMessage(appMessages.sidebarWeighScale),
            active: false,
            value: `${ROUTES.WEIGHT_SCALE}`,
            permission: [Permissions.WASTEMANAGE_WEIGHTSCALE],
          },
          {
            text: formatMessage(appMessages.sidebarWeightingReports),
            active: false,
            value: `${ROUTES.WEIGHTING_REPORTS}`,
            permission: [Permissions.WASTEMANAGE_WEIGHTINGREPORTS],
          },
          {
            text: formatMessage(appMessages.sidebarVehicles),
            active: false,
            value: `${ROUTES.VEHICLES}`,
            permission: [Permissions.WASTEMANAGE_VEHICLES],
          },
          {
            text: formatMessage(appMessages.sidebarLoadType),
            active: false,
            value: `${ROUTES.LOAD_TYPE}`,
            permission: [Permissions.WASTEMANAGE_LOADTYPE],
          },
          {
            text: formatMessage(appMessages.sidebarWasteType),
            active: false,
            value: `${ROUTES.WASTE_TYPE}`,
            permission: [Permissions.WASTEMANAGE_WASTETYPE],
          },
          {
            text: formatMessage(appMessages.sidebarTippingYard),
            active: false,
            value: `${ROUTES.TIPPING_YARD}`,
            permission: [Permissions.WASTEMANAGE_TIPPING_YARD],
          },
          {
            text: formatMessage(appMessages.sidebarSettings),
            active: false,
            value: `${ROUTES.WASTE_SUMMARY_SETTINGS}`,
            permission: [Permissions.WASTEMANAGE_SETTINGS],
          },
        ],
      },
      {
        icon: <FileMinus size={20} />,
        text: formatMessage(appMessages.sidebarReports),
        value: `${ROUTES.REPORTS}`,
        hasChild: true,
        permission: [
          Permissions.REPORTS,
          Permissions.REPORTS_PROJECT_DAILYREPORT,
          Permissions.DAILY_REPORT,
          Permissions.REPORTS_WORKDOCKET,
          Permissions.REPORTS_TIPPING,
          Permissions.REPORTS_REPORT_QUESTION,
          Permissions.REPORTS_REPORT_SETTINGS,
        ],
        children: [
          {
            text: formatMessage(appMessages.sidebarProjectDailyReports),
            active: false,
            value: `${ROUTES.PROJECT_DAILY_REPORTS}`,
            permission: [Permissions.REPORTS_PROJECT_DAILYREPORT],
          },
          {
            text: formatMessage(appMessages.sidebarTippingReports),
            active: false,
            value: `${ROUTES.TIPPING_REPORT}`,
            permission: [Permissions.REPORTS_TIPPING],
          },
          {
            text: formatMessage(appMessages.sidebarWorkDocket),
            active: false,
            value: `${ROUTES.WORK_DOCKET}`,
            permission: [Permissions.REPORTS_WORKDOCKET],
          },
          {
            text: formatMessage(appMessages.sidebarDailyReports),
            active: false,
            value: `${ROUTES.DAILY_REPORTS}`,
            permission: [Permissions.DAILY_REPORT],
          },
          {
            text: formatMessage(appMessages.sidebarReportQuestions),
            active: false,
            value: `${ROUTES.REPORT_QUESTIONS}`,
            permission: [Permissions.REPORTS_REPORT_QUESTION],
          },
          {
            text: formatMessage(appMessages.sidebarReportSettings),
            active: false,
            value: `${ROUTES.REPORT_SETTINGS}`,
            permission: [Permissions.REPORTS_REPORT_SETTINGS],
          },
        ],
      },
      {
        icon: <Icons name='time-icon' size={20} />,
        text: formatMessage(appMessages.sidebarTimeTracking),
        value: `${ROUTES.TIME_TRACKING}`,
        hasChild: true,
        permission: [
          Permissions.TIMETRACKING,
          Permissions.TIMETRACKING_SUMMARY,
          Permissions.TIMETRACKING_TIMESHEET,
          Permissions.TIMETRACKING_SUBCONTRACTOR,
          Permissions.TIMETRACKING_WORKLOGS,
          Permissions.TIMETRACKING_SETTINGS,
        ],
        children: [
          {
            text: formatMessage(appMessages.sidebarSummary),
            active: false,
            value: `${ROUTES.SUMMARY}`,
            permission: [Permissions.TIMETRACKING_SUMMARY],
          },
          {
            text: formatMessage(appMessages.sidebarEmployee),
            active: false,
            value: `${ROUTES.TIME_SHEET}`,
            permission: [Permissions.TIMETRACKING_TIMESHEET],
          },
          {
            text: formatMessage(appMessages.sidebarSubContractor),
            active: false,
            value: `${ROUTES.SUB_CONTRACTOR}`,
            permission: [Permissions.TIMETRACKING_SUBCONTRACTOR],
          },
          {
            text: formatMessage(appMessages.sidebarWorklogs),
            active: false,
            value: `${ROUTES.WORK_LOG}`,
            permission: [Permissions.TIMETRACKING_WORKLOGS],
          },
          ...(isSuperAdminUser
            ? [
                {
                  text: formatMessage(appMessages.sidebarSettings),
                  active: false,
                  value: `${ROUTES.SETTINGS}`,
                  permission: [Permissions.TIMETRACKING_SETTINGS],
                },
              ]
            : []),
        ],
      },
      {
        icon: <Icons name='intelliflow' size={20} />,
        text: formatMessage(appMessages.sidebarIntelliFlow),
        value: `${ROUTES.INTELLIFLOW}`,
        hasChild: true,
        permission: [Permissions.INTELLIFLOW],
        children: [
          {
            text: formatMessage(appMessages.sidebarDocumentParser),
            active: false,
            value: `${ROUTES.DOCUMENT_PARSER}`,
            permission: [Permissions.INTELLIFLOW],
          },
        ],
      },
      {
        icon: <Icons size={20} name='ware-housing' />,
        text: formatMessage(appMessages.sidebarWarehousing),
        value: `${ROUTES.WARE_HOUSING}`,
        hasChild: false,
        permission: [Permissions.WAREHOUSE],
      },
      {
        icon: <Icons size={20} name='calendar' />,
        text: formatMessage(appMessages.sidebarCalendar),
        value: `${ROUTES.CALENDAR}`,
        hasChild: false,
        permission: [Permissions.CALENDAR],
      },
      {
        icon: <Icons size={20} name='support' />,
        text: formatMessage(appMessages.sidebarSupport),
        value: `${ROUTES.SUPPORT}`,
        hasChild: false,
        permission: [Permissions.SUPPORT],
      },
      {
        icon: <Icons size={20} name='messages' />,
        text: formatMessage(appMessages.sidebarMessage),
        value: `${ROUTES.MESSAGES}`,
        hasChild: false,
        permission: [Permissions.MESSAGES],
      },
      {
        icon: <Icons size={20} name='items' />,
        text: formatMessage(appMessages.sidebarItems),
        value: `${ROUTES.ITEMS}`,
        hasChild: false,
        permission: [Permissions.ITEMS],
      },
      {
        icon: <Icons size={20} name='fleet' />,
        text: formatMessage(appMessages.sidebarFleetRegister),
        value: `${ROUTES.FLEET}`,
        hasChild: false,
        permission: [Permissions.FLEETS],
      },
      {
        icon: <Icons size={20} name='tools' />,
        text: formatMessage(appMessages.sidebarTools),
        value: `${ROUTES.TOOLS}`,
        hasChild: true,
        permission: [Permissions.TOOLS],
        children: [
          {
            text: formatMessage(appMessages.sidebarProducts),
            active: false,
            value: `${ROUTES.PRODUCTS}`,
            permission: [Permissions.TOOLS],
          },
          {
            text: formatMessage(appMessages.sidebarRequest),
            active: false,
            value: `${ROUTES.REQUEST}`,
            permission: [Permissions.TOOLS],
          },
        ],
      },
      {
        icon: <File size={20} />,
        text: formatMessage(appMessages.sidebarOhs),
        value: `${ROUTES.OHS}`,
        hasChild: true,
        permission: [Permissions.OHS],
        children: [
          {
            text: formatMessage(appMessages.sidebarInduction),
            active: false,
            value: `${ROUTES.INDUCTION}`,
            permission: [Permissions.OHS],
          },
        ],
      },
      {
        icon: <Icons size={20} name='setting-icon' />,
        text: formatMessage(appMessages.sidebarSetting),
        value: `${ROUTES.SETTINGS}`,
        hasChild: false,
        permission: [Permissions.SETTING],
      },
      {
        icon: <Icons size={20} name='bell' />,
        text: formatMessage(appMessages.sidebarActivity),
        value: `${ROUTES.ACTIVITY}`,
        alert: false,
        hasChild: false,
        permission: [Permissions.ACTIVITY],
      },
    ];

    return businessSidebarItems.filter((item) => {
      const sidebarValue = item.value ?? "";

      return !HIDDEN_BUSINESS_SIDEBAR_VALUES.has(sidebarValue);
    });
  }, [formatMessage, isSuperAdminUser]);
};

export const useCustomerSidebarData = (): TSidebarItem[] => {
  const { formatMessage } = useIntl();
  const isNotCustomerUser = useCustomerWritePermission();
  const { canAccessProjectFinance, canAccessWorkDocket } =
    useCustomerPortalPermission();

  return [
    {
      icon: <Icons size={20} name='dashboard' />,
      text: formatMessage(appMessages.customerSidebarDashboard),
      value: `${ROUTES.ROOT}`,
      hasChild: false,
      permission: [Permissions.CP_DASHBOARD],
    },
    ...(isNotCustomerUser
      ? [
          {
            icon: <Calendar size={16} />,
            text: formatMessage(appMessages.customerSidebarUserManagement),
            value: `${ROUTES.USER_MANAGEMENT}`,
            hasChild: false,
            permission: [Permissions.CP_USERMANAGEMENT],
          },
        ]
      : []),
    {
      icon: <Icons size={20} name='projects' />,
      text: formatMessage(appMessages.customerSidebarProjects),
      value: `${ROUTES.PROJECTS}`,
      hasChild: false,
      permission: [Permissions.CP_PROJECTS],
    },
    ...(canAccessProjectFinance
      ? [
          {
            icon: <FileMinus size={20} />,
            text: formatMessage(appMessages.customerSidebarInvoices),
            value: `${ROUTES.INVOICING}`,
            hasChild: false,
            permission: [Permissions.CP_INVOICES],
          },
        ]
      : []),
    ...(canAccessWorkDocket
      ? [
          {
            icon: <Icons size={20} name='projects' />,
            text: formatMessage(appMessages.customerSidebarWorkDocket),
            value: `${ROUTES.WORK_DOCKET}`,
            hasChild: false,
            permission: [Permissions.CP_WORKDOCKETS],
          },
        ]
      : []),
    {
      icon: <Icons size={20} name='waste-manage' />,
      text: formatMessage(appMessages.customerSidebarWasteManagement),
      value: `${ROUTES.WASTE}`,
      hasChild: true,
      permission: [
        Permissions.CP_WASTEMANAGEMENT,
        Permissions.CP_WASTE_SUMMARY,
        Permissions.CP_WASTE_REQUEST,
      ],
      children: [
        {
          text: formatMessage(appMessages.sidebarSummary),
          active: false,
          value: `${ROUTES.WASTE_SUMMARY}`,
          permission: [Permissions.CP_WASTE_SUMMARY],
        },
        {
          text: formatMessage(appMessages.customerSidebarRequests),
          active: false,
          value: `${ROUTES.WASTE_REQUEST}`,
          permission: [Permissions.CP_WASTE_REQUEST],
        },
      ],
    },
  ];
};
